import React, { useState, useEffect, useRef } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { formatPhoneNumber } from "src/utils/formatUtils";
import {
  PRODUCT_TYPES,
  SUBMISSION_METHODS,
  formatTimestamp,
} from "./constants";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import {
  IReassignedSession,
  IReassignedSessionRecord,
  IReassignedSessionRecordsResponse,
} from "src/interfaces/Reports/IReassignedSession";

// --- Props ---

interface ReassignedSessionDetailProps {
  session: IReassignedSession;
  onClose: () => void;
}

// --- Component ---

const ReassignedSessionDetail: React.FC<ReassignedSessionDetailProps> = ({
  session,
  onClose,
}) => {
  // Grid state
  const [records, setRecords] = useState<IReassignedSessionRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState({ skip: 0, take: 50 });

  const requestIdRef = useRef(0);

  // --- Effects ---

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    const fetchRecords = async () => {
      setIsLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        params.append("skip", String(page.skip));
        params.append("take", String(page.take));

        const response: IReassignedSessionRecordsResponse = await callApi(
          `reports/reassigned-sessions/${session.sessionId}/records?${params.toString()}`,
          HttpMethod.GET,
        );

        if (requestId !== requestIdRef.current) return;

        setRecords(response.data || []);
        setTotal(response.total || 0);
      } catch (err: any) {
        if (requestId !== requestIdRef.current) return;
        console.error("Error fetching session records:", err);
        setError(`Error fetching records: ${err.message || err}`);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchRecords();
  }, [page, session.sessionId]);

  // --- Handlers ---

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({ skip: event.page.skip, take: event.page.take });
  };

  // --- Custom Cells ---

  const PhoneCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    return <td>{formatPhoneNumber(dataItem.phoneNumber)}</td>;
  };

  const DateCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!dataItem || !field) return <td>-</td>;
    return <td>{formatTimestamp(dataItem[field])}</td>;
  };

  const BooleanCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!dataItem || !field) return <td>-</td>;
    const value = dataItem[field];
    return (
      <td>
        <span className={`badge ${value ? "bg-success" : "bg-secondary"}`}>
          {value ? "Yes" : "No"}
        </span>
      </td>
    );
  };

  // --- Render ---

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-detail-title"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-dialog-scrollable"
        style={{ maxWidth: "95vw", height: "90vh" }}
      >
        <div
          className="modal-content"
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="session-detail-title">
              Session Details
              <small className="text-muted ms-2">
                #{session.sessionId}
                {session.receivedBy !== "a" && ` - ${session.fileName}`}
              </small>
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
            ></button>
          </div>
          <div
            className="modal-body"
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {/* Session Summary */}
            <div className="row g-3 mb-3">
              <div className="col-md-2">
                <div className="card bg-light">
                  <div className="card-body py-2">
                    <small className="text-muted">Date</small>
                    <div>
                      <strong>{formatTimestamp(session.addedOn)}</strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-light">
                  <div className="card-body py-2">
                    <small className="text-muted">Total Records</small>
                    <div>
                      <strong>{session.totalRecords.toLocaleString()}</strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-light">
                  <div className="card-body py-2">
                    <small className="text-muted">Total Reassigned</small>
                    <div>
                      <strong>
                        {session.totalIsReassigned.toLocaleString()}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-light">
                  <div className="card-body py-2">
                    <small className="text-muted">Product</small>
                    <div>
                      <strong>
                        {PRODUCT_TYPES[session.reassignedProductId] ||
                          "Unknown"}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-light">
                  <div className="card-body py-2">
                    <small className="text-muted">Method</small>
                    <div>
                      <strong>
                        {SUBMISSION_METHODS[session.receivedBy] ||
                          session.receivedBy}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-light">
                  <div className="card-body py-2">
                    <small className="text-muted">Campaign</small>
                    <div>
                      <strong>{session.campaignName || "-"}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Records Grid */}
            <h6 className="mb-2">
              Records{" "}
              <span className="text-muted">
                ({total.toLocaleString()} total)
              </span>
            </h6>

            {error && <div className="alert alert-danger">{error}</div>}

            {isLoading && (
              <div className="text-center py-3">
                <div
                  className="spinner-border text-primary spinner-border-sm"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-2">Loading records...</span>
              </div>
            )}

            {!isLoading && records.length === 0 && !error && (
              <div className="alert alert-info">No records found.</div>
            )}

            {records.length > 0 && (
              <Grid
                data={records}
                total={total}
                pageable={{
                  buttonCount: 5,
                  pageSizes: [25, 50, 100],
                }}
                skip={page.skip}
                take={page.take}
                onPageChange={handlePageChange}
                resizable={true}
                style={{ flex: 1, minHeight: 0 }}
              >
                <Column
                  field="phoneNumber"
                  title="Phone Number"
                  width={150}
                  cell={PhoneCell}
                />
                <Column
                  field="dateOfLastContact"
                  title="Consent Date"
                  width={170}
                  cell={DateCell}
                />
                <Column
                  field="isReassigned"
                  title="Is Reassigned"
                  width={120}
                  cell={BooleanCell}
                />
                {session.reassignedProductId === 1 && (
                  <Column
                    field="isValid"
                    title="Is Valid"
                    width={100}
                    cell={BooleanCell}
                  />
                )}
                {session.reassignedProductId === 1 && (
                  <Column field="lineType" title="Line Type" width={120} />
                )}
                <Column
                  field="carrier"
                  title="Carrier"
                  minResizableWidth={150}
                />
                <Column
                  field="cccIsReassigned"
                  title="CCC Reassigned"
                  width={150}
                  cell={BooleanCell}
                />
                <Column
                  field="hasSafeHarbor"
                  title="Safe Harbor"
                  width={110}
                  cell={BooleanCell}
                />
              </Grid>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReassignedSessionDetail;
